import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { StripeElementStyleVariant } from '@stripe/stripe-js';
import { Alert, Button, Input, WithLabel } from 'easyship-components';
import { ChangeEvent, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { usePaymentIntentContext } from '@/providers/PaymentIntentProvider/context';
import PoweredByStripeSVG from '@/svg/PoweredByStripe.svg';

const CARD_BASE_STYLE: StripeElementStyleVariant = {
  backgroundColor: '#fff',
  color: '#454F5B',
  fontFamily: '"Karla", sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '40px',
};

export const CreditCardForm = () => {
  const { formatMessage } = useIntl();
  const stripe = useStripe();
  const elements = useElements();

  const {
    data: { insurance_fee, stripe_payment_intent },
    confirmPaymentIntent,
    failPaymentIntent,
  } = usePaymentIntentContext();

  const [cardholderName, setCardholderName] = useState('');
  const [processingError, setProcessingError] = useState<string>();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCardholderNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setCardholderName(event.target.value);
  }, []);

  const handlePayNow = useCallback(async () => {
    const card = elements?.getElement(CardNumberElement);

    if (!stripe || !elements || !stripe_payment_intent || !card) {
      return;
    }

    if (!cardholderName) {
      setProcessingError(formatMessage({ id: 'credit-card.cardholder-name-required' }));
      return;
    }

    setIsProcessing(true);

    try {
      const { error: cardPaymentError, paymentIntent } = await stripe.confirmCardPayment(
        stripe_payment_intent.client_secret,
        {
          payment_method: {
            card,
            billing_details: {
              name: cardholderName,
            },
          },
        },
      );

      if (cardPaymentError) {
        setProcessingError(
          cardPaymentError.message ||
          formatMessage({ id: 'credit-card.processing-error' }),
        );
        setIsProcessing(false);
        failPaymentIntent(); // Notify backend of payment failure
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        try {
          await confirmPaymentIntent();
          setProcessingError(undefined);
          setIsProcessing(false);
        } catch (confirmPaymentIntentError) {
          setProcessingError(
            confirmPaymentIntentError.response?.data.error ||
            confirmPaymentIntentError.message ||
            formatMessage({ id: 'credit-card.confirm-error' }),
          );
          setIsProcessing(false);
        }
      }
    } catch (error) {
      setProcessingError(
        formatMessage({ id: 'credit-card.unexpected-processing-error' }),
      );
      setIsProcessing(false);
    }
  }, [
    cardholderName,
    confirmPaymentIntent,
    elements,
    failPaymentIntent,
    formatMessage,
    stripe,
    stripe_payment_intent,
  ]);

  if (!stripe || !elements || !stripe_payment_intent) {
    return null;
  }

  return (
    <>
      <div className="flex items-center justify-between py-2 font-bold">
        <FormattedMessage id="credit-card.insurance-cost" />
        <span>{insurance_fee}</span>
      </div>
      {processingError && <Alert severity="error">{processingError}</Alert>}
      <div
        role="form"
        aria-label={formatMessage({ id: 'credit-card.form-aria-label' })}
        className="flex flex-col gap-4 items-start"
      >
        <WithLabel value={<FormattedMessage id="credit-card.card-number" />} className="w-full">
          <div className="flex flex-wrap rounded-[4px] border px-2 w-full bg-white border-sky-500">
            <div className="w-full sm:w-3/5">
              <CardNumberElement
                options={{
                  disableLink: true,
                  showIcon: true,
                  style: {
                    base: {
                      ...CARD_BASE_STYLE,
                      '::placeholder': {
                        color: 'transparent', // Hide placeholder text
                      },
                    },
                  },
                }}
              />
            </div>
            <div className="w-1/2 sm:w-1/5">
              <CardExpiryElement
                options={{
                  style: {
                    base: CARD_BASE_STYLE,
                  },
                }}
              />
            </div>
            <div className="w-1/2 sm:w-1/5">
              <CardCvcElement
                options={{
                  style: {
                    base: CARD_BASE_STYLE,
                  },
                }}
              />
            </div>
          </div>
        </WithLabel>

        <Input
          value={cardholderName}
          onChange={handleCardholderNameChange}
          label={<FormattedMessage id="credit-card.cardholder-name" />}
        />

        <img src={PoweredByStripeSVG} className="h-7" />
      </div>
      <Button
        className="w-full"
        color="primary"
        disabled={!stripe || isProcessing}
        loading={isProcessing}
        onClick={handlePayNow}
      >
        <FormattedMessage id="credit-card.pay-now" />
      </Button>
    </>
  );
};
